import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { ServerNATInfo } from '@/api/type-server'
import SparkIcon from '@/assets/icons/spark.svg?url'
import { Button, Trans } from '@/components'
import { MachineType, paymentHours, PaymentType } from '@/consts/machine'
import { Machine } from '@/models/machine'
import { formatPrice } from '@/utils/formatter'
import { formatHwConfig } from '@/utils/node'
import { cn } from '@/utils/style'

interface SummaryProps {
  type?: MachineType
  period?: PaymentType
  count?: number
  machine?: Machine
  nat?: ServerNATInfo
  disabled?: boolean
  onConfirm?: () => void
  onDetailToggle?: (showDetail: boolean) => void
}

type OrderSummaryItemProps = {
  label: string | ReactNode
  value?: any
  isTechnicalPreview?: boolean
  formatter?: (value: any) => any
}

export function OrderSummaryItem({
  label,
  value,
  isTechnicalPreview = false,
  formatter = (v) => v,
}: OrderSummaryItemProps) {
  return (
    <div className="flex items-center gap-2 border-b border-gray100 py-3">
      <div className="t-sm text-secondary min-w-[120px]">{label}</div>
      <div
        className={cn('t-sm', {
          'text-primary': value !== undefined,
          'text-tertiary': value === undefined,
        })}
      >
        {value !== undefined ? formatter(value) : '-'}
      </div>

      {isTechnicalPreview && (
        <div className="px-2 py-0.5 border border-neutral-200 bg-neutral-50 rounded-full flex items-center justify-center gap-x-1">
          <img src={SparkIcon} alt="spark" className="w-4 h-4" />
          <div className="t-xs bg-gradient-to-r from-[#717784] via-[#B1B4BB] to-[#717784] bg-clip-text text-transparent">
            <Trans i18nKey="create.bindPublicIpTechnicalPreview">Technical Preview</Trans>
          </div>
        </div>
      )}
    </div>
  )
}

export function OrderSummary({
  type,
  period,
  machine,
  nat,
  count,
  disabled,
  onConfirm,
  onDetailToggle,
}: SummaryProps) {
  const [showDetail, setShowDetail] = useState(false)
  const detailRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLDivElement>(null)

  const price = useMemo(() => {
    if (!machine || !count || typeof period === 'undefined') {
      return 0
    }
    return machine.priceObject.price * paymentHours[period] * count
  }, [machine, type, count, period])

  const hw = useMemo(() => (machine ? formatHwConfig(machine.unitSpec) : undefined), [machine])

  const details: OrderSummaryItemProps[] = [
    { label: <Trans i18nKey="create.summaryLabelUnitQty">Unit Qty</Trans>, value: count },
    ...(type && Machine.getMachineType(type) === MachineType.GPU
      ? [{ label: <Trans i18nKey="create.summaryLabelGpu">GPU Model</Trans>, value: hw?.gpuName }]
      : []),
    { label: <Trans i18nKey="create.summaryLabelCpu">CPU Model</Trans>, value: hw?.cpuName },
    ...(type && Machine.getMachineType(type) === MachineType.GPU
      ? [
          {
            label: <Trans i18nKey="create.summaryLabelGpus">GPUs</Trans>,
            value: machine?.unitSpec.gpuCores,
          },
          {
            label: <Trans i18nKey="create.summaryLabelGPURAM">GPU RAM</Trans>,
            value: hw?.gpuRamGB,
            formatter: (ram: number) => `${ram} GB`,
          },
        ]
      : []),
    {
      label: <Trans i18nKey="create.summaryLabelVCpus">vCPUs</Trans>,
      value: hw?.cpuCores,
      formatter: (cores: number) => `${cores * (count ?? 1)}`,
    },
    {
      label: <Trans i18nKey="create.summaryLabelRAM">RAM</Trans>,
      value: hw?.cpuRamGB,
      formatter: (ram: number) => `${ram * (count ?? 1)} GB`,
    },
    {
      label: <Trans i18nKey="create.summaryLabelStorage">Storage</Trans>,
      value: hw?.diskGB,
      formatter: (disk: number) => `${disk * (count ?? 1)} GB`,
    },
    ...(nat
      ? [
          {
            label: <Trans i18nKey="create.summaryLabelPublicIP">Public IP</Trans>,
            value: nat?.name ?? '-',
            isTechnicalPreview: true,
          },
        ]
      : []),
  ]

  useEffect(() => {
    onDetailToggle?.(showDetail)
  }, [showDetail, onDetailToggle])

  const toggleDetail = () => {
    setShowDetail((prev) => !prev)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        detailRef.current &&
        !detailRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDetail(false)
        onDetailToggle?.(false)
      }
    }

    if (showDetail) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDetail])

  return (
    <>
      <div
        ref={detailRef}
        className={cn('overflow-hidden transition-all duration-300', {
          'mb-8': showDetail,
          'max-h-0': !showDetail,
        })}
      >
        <div className="mb-8 flex-1 flex flex-col">
          <div className="t-bold text-primary mb-2">
            <Trans i18nKey="create.summaryLabelBase">Base</Trans>
          </div>

          <OrderSummaryItem
            label={<Trans i18nKey="create.summaryLabelType">Type</Trans>}
            value={type}
            formatter={(type) => (type === MachineType.CPU ? 'CPU' : 'GPU')}
          />
        </div>
        <div>
          <div className="t-bold text-primary mb-2">
            <Trans i18nKey="create.summaryLabelMachine">Machine</Trans>
          </div>

          <div className="flex flex-col">
            {details.map((detail, index) => (
              <OrderSummaryItem
                key={index}
                label={detail.label}
                value={detail.value}
                formatter={detail.formatter}
                isTechnicalPreview={detail.isTechnicalPreview}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="flex gap-6">
          <div className="flex gap-2 items-center">
            <div className="t-sm text-secondary">
              <Trans i18nKey="create.summaryLabelPrice">Price/h</Trans>
            </div>
            <div className="h6 text-panel-primary">
              {formatPrice(price, false, price > 100 ? 'Credits' : 'Credit')}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <div className="t-sm text-secondary">
              <Trans i18nKey="create.summaryLabelQuantity">Instance Ordered</Trans>
            </div>
            <div className="h6 text-panel-primary">{machine ? 1 : '-'}</div>
          </div>
        </div>

        <div className="flex gap-2 justify-end items-center">
          <div
            ref={buttonRef}
            onClick={machine !== undefined ? toggleDetail : undefined}
            className="t-sb text-highlight px-4 py-3 cursor-pointer relative text-nowrap"
          >
            <span className="invisible">Show Detail</span>

            <span
              className={cn('absolute inset-0 flex items-center px-4 transition-all duration-300', {
                'opacity-0 -translate-x-4': showDetail,
                'opacity-100 translate-x-0': !showDetail,
                'text-secondary': machine === undefined,
              })}
            >
              <Trans i18nKey="create.summaryShowDetail">Show Detail</Trans>
            </span>
            <span
              className={cn('absolute inset-0 flex items-center px-4 transition-all duration-300', {
                'opacity-100 translate-x-0': showDetail,
                'opacity-0 translate-x-4': !showDetail,
              })}
            >
              <Trans i18nKey="create.summaryHideDetail">Hide Detail</Trans>
            </span>
          </div>
          <div>
            <Button type="secondary" block disabled={disabled} onClick={onConfirm}>
              <Trans i18nKey="create.complete">Complete Checkout</Trans>
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
